import React from 'react';
import { Divider, Grid, Header, Message } from 'semantic-ui-react';

import { maskCpfOrCnpj } from '../../../services/masks';
import { getActivationValue } from '../utils/activationFormHelpers';
import FieldBlock from './FieldBlock';
import {
  formatBankAccount,
  formatBirthdate,
  formatDateTime,
  formatMonthlyIncome,
  formatZipCodeDisplay,
  pickRegisterAndBankFromActivation,
  ActivationDisplaySource
} from '../utils/activationDetailsHelpers';

type Props = {
  source?: ActivationDisplaySource | null;
  createdAt?: string | Date | null;
  updatedAt?: string | Date | null;
};

const ActivationRegistrationSections: React.FC<Props> = ({
  source,
  createdAt,
  updatedAt
}) => {
  const activationData = pickRegisterAndBankFromActivation(source);

  return (
    <>
      <Header as="h4" style={{ marginBottom: 16 }}>
        {activationData.isCorporation ? 'Dados da empresa' : 'Dados cadastrais'}
      </Header>
      {activationData.isCorporation ? (
        <Grid stackable columns={3}>
          <Grid.Column>
            <FieldBlock label="Razão social">
              {getActivationValue(activationData.registerInformation, 'company_name') || '-'}
            </FieldBlock>
          </Grid.Column>
          <Grid.Column>
            <FieldBlock label="Nome fantasia">
              {getActivationValue(activationData.registerInformation, 'trading_name') || '-'}
            </FieldBlock>
          </Grid.Column>
          <Grid.Column>
            <FieldBlock label="Receita anual">
              {formatMonthlyIncome(
                getActivationValue(activationData.registerInformation, 'annual_revenue')
              )}
            </FieldBlock>
          </Grid.Column>
          <Grid.Column>
            <FieldBlock label="E-mail">
              {getActivationValue(activationData.registerInformation, 'email') || '-'}
            </FieldBlock>
          </Grid.Column>
          <Grid.Column>
            <FieldBlock label="Documento">
              {(() => {
                const document = getActivationValue(
                  activationData.registerInformation,
                  'document'
                );
                return document ? maskCpfOrCnpj(document) : '-';
              })()}
            </FieldBlock>
          </Grid.Column>
        </Grid>
      ) : (
        <Grid stackable columns={3}>
          <Grid.Column>
            <FieldBlock label="Nome">
              {getActivationValue(activationData.registerInformation, 'name') || '-'}
            </FieldBlock>
          </Grid.Column>
          <Grid.Column>
            <FieldBlock label="E-mail">
              {getActivationValue(activationData.registerInformation, 'email') || '-'}
            </FieldBlock>
          </Grid.Column>
          <Grid.Column>
            <FieldBlock label="Documento">
              {(() => {
                const document = getActivationValue(
                  activationData.registerInformation,
                  'document'
                );
                return document ? maskCpfOrCnpj(document) : '-';
              })()}
            </FieldBlock>
          </Grid.Column>
          <Grid.Column>
            <FieldBlock label="Data de nascimento">
              {formatBirthdate(
                getActivationValue(activationData.registerInformation, 'birthdate')
              )}
            </FieldBlock>
          </Grid.Column>
          <Grid.Column>
            <FieldBlock label="Renda mensal">
              {formatMonthlyIncome(
                getActivationValue(activationData.registerInformation, 'monthly_income')
              )}
            </FieldBlock>
          </Grid.Column>
          <Grid.Column>
            <FieldBlock label="Ocupação profissional">
              {getActivationValue(
                activationData.registerInformation,
                'professional_occupation'
              ) || '-'}
            </FieldBlock>
          </Grid.Column>
        </Grid>
      )}

      {activationData.isCorporation && activationData.partner && (
        <>
          <Divider section />
          <Header as="h4" style={{ marginBottom: 16 }}>
            Representante legal
          </Header>
          <Grid stackable columns={3}>
            <Grid.Column>
              <FieldBlock label="Nome">
                {getActivationValue(activationData.partner, 'name') || '-'}
              </FieldBlock>
            </Grid.Column>
            <Grid.Column>
              <FieldBlock label="CPF">
                {getActivationValue(activationData.partner, 'document')
                  ? maskCpfOrCnpj(getActivationValue(activationData.partner, 'document'))
                  : '-'}
              </FieldBlock>
            </Grid.Column>
            <Grid.Column>
              <FieldBlock label="E-mail">
                {getActivationValue(activationData.partner, 'email') || '-'}
              </FieldBlock>
            </Grid.Column>
          </Grid>
        </>
      )}

      <Divider section />

      <Header as="h4" style={{ marginBottom: 16 }}>
        Endereço
      </Header>
      <Grid stackable columns={3}>
        <Grid.Column width={6}>
          <FieldBlock label="Logradouro">
            {[
              getActivationValue(activationData.address, 'street'),
              getActivationValue(activationData.address, 'street_number')
            ]
              .filter(Boolean)
              .join(', ') || '-'}
          </FieldBlock>
          <FieldBlock label="Complemento">
            {getActivationValue(activationData.address, 'complementary') || '-'}
          </FieldBlock>
        </Grid.Column>
        <Grid.Column width={5}>
          <FieldBlock label="Bairro">
            {getActivationValue(activationData.address, 'neighborhood') || '-'}
          </FieldBlock>
          <FieldBlock label="CEP">{formatZipCodeDisplay(activationData.address)}</FieldBlock>
        </Grid.Column>
        <Grid.Column width={5}>
          <FieldBlock label="Cidade">
            {getActivationValue(activationData.address, 'city') || '-'}
          </FieldBlock>
          <FieldBlock label="UF">
            {getActivationValue(activationData.address, 'state') || '-'}
          </FieldBlock>
        </Grid.Column>
      </Grid>

      <Divider section />

      <Header as="h4" style={{ marginBottom: 16 }}>
        Dados bancários
      </Header>
      <Grid stackable columns={2}>
        <Grid.Column>
          <FieldBlock label="Titular">
            {getActivationValue(activationData.defaultBankAccount, 'holder_name') || '-'}
          </FieldBlock>
          <FieldBlock label="Conta">
            {formatBankAccount(activationData.defaultBankAccount)}
          </FieldBlock>
        </Grid.Column>
        <Grid.Column>
          <FieldBlock label="Documento do titular">
            {(() => {
              const document = getActivationValue(
                activationData.defaultBankAccount,
                'holder_document'
              );
              return document ? maskCpfOrCnpj(document) : '-';
            })()}
          </FieldBlock>
        </Grid.Column>
      </Grid>

      <Divider section />

      <Header as="h4" style={{ marginBottom: 16 }}>
        Ativação Pagar.me
      </Header>
      <Grid stackable columns={3}>
        <Grid.Column>
          <FieldBlock label="ID do recebedor">
            <code style={{ fontSize: '0.85em', wordBreak: 'break-all' }}>
              {activationData.pagarmeRecipientId || '-'}
            </code>
          </FieldBlock>
          <FieldBlock label="Status da última sincronização">
            {activationData.lastSyncStatus || '-'}
          </FieldBlock>
        </Grid.Column>
        <Grid.Column>
          <FieldBlock label="Última sincronização">
            {formatDateTime(activationData.lastSyncAt)}
          </FieldBlock>
          <FieldBlock label="Conclusão da ativação">
            {formatDateTime(activationData.completedAt)}
          </FieldBlock>
        </Grid.Column>
        <Grid.Column>
          <FieldBlock label="Criação do cadastro">
            {formatDateTime(createdAt ?? activationData.createdAt)}
          </FieldBlock>
          <FieldBlock label="Última atualização">
            {formatDateTime(updatedAt ?? activationData.updatedAt)}
          </FieldBlock>
        </Grid.Column>
      </Grid>
      {activationData.lastSyncError && (
        <Message warning style={{ marginTop: 8 }}>
          <Message.Header>Erro na última sincronização</Message.Header>
          <p style={{ margin: '0.5em 0 0', whiteSpace: 'pre-wrap' }}>
            {activationData.lastSyncError}
          </p>
        </Message>
      )}
    </>
  );
};

export default ActivationRegistrationSections;
